import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Home, Menu, X } from 'lucide-react'
import blackLogo from '../assets/brit-logo.svg'
import userInfo from '../assets/user-info-icon.svg'

interface NavHeaderProps {
  activeTab?: string
}

export default function NavHeader({ activeTab = 'home' }: NavHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const isOnLandingPage = location.pathname === '/'

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open')
    } else {
      document.body.classList.remove('menu-open')
    }
    return () => {
      document.body.classList.remove('menu-open')
    }
  }, [isMobileMenuOpen])

  const scrollToSection = (sectionId: string) => {
    if (isOnLandingPage) {
      const element = document.getElementById(sectionId)
      if (element) {
        const headerOffset = 80
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
    } else {
      navigate(`/#${sectionId}`)
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          const headerOffset = 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffset
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
      }, 100)
    }
  }

  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  const renderNavButton = (
    id: string,
    label: string,
    onClick: () => void,
    showHomeIcon = false
  ) => {
    const isActive = activeTab === id
    if (isActive) {
      return (
        <button
          onClick={onClick}
          className='flex items-center gap-2.5 bg-[#001B38] text-white rounded-lg px-[19px] py-2.5 font-bold text-base leading-6 cursor-pointer'
          style={{ fontFamily: '"DM Sans", sans-serif' }}
        >
          {showHomeIcon && <Home className='w-[18px] h-[18px]' strokeWidth={1.5} />}
          <span>{label}</span>
        </button>
      )
    }
    return (
      <button
        onClick={onClick}
        className='flex items-center px-[19px] py-2.5 text-[#0D0D0F] font-bold text-base leading-6 hover:opacity-70 transition-opacity cursor-pointer'
        style={{ fontFamily: '"DM Sans", sans-serif' }}
      >
        <span>{label}</span>
      </button>
    )
  }

  return (
    <nav className='relative z-50 w-full bg-white'>
      <div className='mx-auto max-w-[1920px] flex items-center justify-between gap-6 px-4 sm:px-6 lg:px-20 py-4 lg:py-[29px]'>
        {/* Logo */}
        <button
          onClick={() => navigate('/')}
          className='cursor-pointer flex-shrink-0'
          aria-label='BritFiler home'
        >
          <img
            src={blackLogo}
            alt='BritFiler Logo'
            className='h-[34px] sm:h-[40px] lg:h-[48px] xl:h-[53px] w-auto'
          />
        </button>

        {/* Desktop nav */}
        <div className='hidden lg:flex items-center gap-1 xl:gap-2'>
          {renderNavButton('home', 'Home', () => navigate('/'), true)}
          {renderNavButton('about', 'About Us', () => navigate('/about'))}
          {renderNavButton('features', 'Features', () => navigate('/features'))}
          {renderNavButton('pricing', 'Pricing', () => scrollToSection('pricing'))}
          {renderNavButton('help-center', 'Help Center', () => navigate('/help-center'))}
          {renderNavButton('blog', 'Blog', () => scrollToSection('blog'))}
        </div>

        {/* Right CTA group */}
        <div className='hidden lg:flex items-center gap-6 xl:gap-[34px] flex-shrink-0'>
          <button
            onClick={() => navigate('/login')}
            className='flex items-center gap-[9px] cursor-pointer hover:opacity-70 transition-opacity'
          >
            <img src={userInfo} alt='' className='h-5 w-4' />
            <span
              className='text-[#181D27] font-bold text-base leading-6'
              style={{ fontFamily: '"DM Sans", sans-serif' }}
            >
              Login
            </span>
          </button>
          <button
            onClick={() => navigate('/sign-up')}
            className='bg-[#181D27] h-[52px] w-[164px] rounded-[14px] text-white text-base font-semibold leading-6 hover:opacity-90 transition-opacity cursor-pointer'
            style={{
              fontFamily: '"DM Sans", sans-serif',
              boxShadow:
                '0px 106px 42px 0px rgba(0,0,0,0.04), 0px 165px 46px 0px rgba(0,0,0,0.01)',
            }}
          >
            Get Started
          </button>
        </div>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className='lg:hidden p-2 text-gray-700 cursor-pointer'
          aria-label='Toggle menu'
        >
          {isMobileMenuOpen ? (
            <X className='w-6 h-6' />
          ) : (
            <Menu className='w-6 h-6' />
          )}
        </button>
      </div>

      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div
          className='fixed inset-0 bg-black/50 z-30 lg:hidden'
          onClick={closeMobileMenu}
          aria-hidden='true'
        />
      )}

      {/* Mobile menu drawer */}
      {isMobileMenuOpen && (
        <div
          className='absolute top-full left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 shadow-lg max-h-[calc(100vh-80px)] overflow-y-auto'
          onClick={e => e.stopPropagation()}
        >
          <div className='px-4 sm:px-6 py-4 space-y-2'>
            {[
              { id: 'home', label: 'Home', action: () => navigate('/') },
              { id: 'about', label: 'About Us', action: () => navigate('/about') },
              { id: 'features', label: 'Features', action: () => navigate('/features') },
              { id: 'pricing', label: 'Pricing', action: () => scrollToSection('pricing') },
              // { id: 'help-center', label: 'Help Center', action: () => navigate('/help-center') },
              { id: 'blog', label: 'Blog', action: () => scrollToSection('blog') },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => {
                  item.action()
                  closeMobileMenu()
                }}
                className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#001B38] text-white'
                    : 'text-[#0D0D0F] hover:bg-gray-50'
                }`}
                style={{ fontFamily: '"DM Sans", sans-serif' }}
              >
                {item.label}
              </button>
            ))}
            <div className='border-t border-gray-200 pt-3 mt-3 space-y-2'>
              <button
                onClick={() => {
                  navigate('/login')
                  closeMobileMenu()
                }}
                className='flex items-center gap-2 w-full px-4 py-2.5 text-sm font-bold text-[#181D27] hover:bg-gray-50 rounded-lg'
                style={{ fontFamily: '"DM Sans", sans-serif' }}
              >
                <img src={userInfo} alt='' className='h-5 w-4' />
                Login
              </button>
              <button
                onClick={() => {
                  navigate('/sign-up')
                  closeMobileMenu()
                }}
                className='block w-full text-center px-4 py-3 bg-[#181D27] text-white rounded-[14px] text-base font-semibold hover:opacity-90'
                style={{ fontFamily: '"DM Sans", sans-serif' }}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
